import { match } from 'ts-pattern';
import { Granularity } from '../../../../common/date-manager/date-manager-constants';
import { getStartOfFiscalYear } from '../../../../common/date-manager/date-manager-utils';
import { TimeSliderConfig } from '../../../../common/filter/filter-store';
import {
  ApiMasterDataType,
  TimeSelectionInput,
} from '../../../../common/graphql/generated/graphql-sdk';
import {
  getFiscalQuarter,
  getMomentDate,
} from '../../../../common/v2/date/date-utils';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
} from '../../../../common/v2/types';

const fieldsOfIntValue: DataFieldWithDataType[] = [
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.AGE },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.JOINING_AGE },
];

const isIntValue = (dataFieldWithDataType: DataFieldWithDataType) => {
  return fieldsOfIntValue.deepCompareContains(dataFieldWithDataType);
};

export const singleNonNullValueFilter = <T>(
  v: T,
  dataFieldWithDataType: DataFieldWithDataType
) => ({
  bigDecimal: null,
  date: null,
  double: null,
  int: isIntValue(dataFieldWithDataType) ? Number(v) : null,
  long: null,
  null: null,
  json: null,
  string: !isIntValue(dataFieldWithDataType) ? v : null,
});

export const singleNullValueFilter = () => ({
  bigDecimal: null,
  date: null,
  double: null,
  int: null,
  long: null,
  null: true,
  string: null,
  json: null,
});

export const dataTypesToApiMasterDataTypeMapper = (dataType: DataTypes) => {
  return match(dataType)
    .with(DataTypes.APPLICATION, () => ApiMasterDataType.Application)
    .with(DataTypes.EMPLOYEE, () => ApiMasterDataType.Employee)
    .with(DataTypes.EVALUATION, () => ApiMasterDataType.Evaluation)
    .with(DataTypes.FINANCIALMETRICS, () => ApiMasterDataType.Financialmetrics)
    .with(DataTypes.INTERVIEW, () => ApiMasterDataType.Interview)
    .with(DataTypes.JOB, () => ApiMasterDataType.Job)
    .with(DataTypes.JOINERS_VIEW, () => ApiMasterDataType.JoinersView)
    .with(DataTypes.OFFER, () => ApiMasterDataType.Offer)
    .with(DataTypes.PAYROLL, () => ApiMasterDataType.Payroll)
    .with(DataTypes.QUESTIONANSWER, () => ApiMasterDataType.Questionanswer)
    .with(DataTypes.RECRUITERJOBS, () => ApiMasterDataType.Recruiterjobs)
    .with(
      DataTypes.TIMEANDATTENDANCEMONTHLY,
      () => ApiMasterDataType.Timeandattendancemonthly
    )
    .with(DataTypes.MANAGERENPS, () => ApiMasterDataType.Managerenps)
    .with(DataTypes.COHORT, () => ApiMasterDataType.Cohort)
    .exhaustive();
};

export const timeSliderGranularityToTimeSelectionInput = (
  timeSliderConfig: TimeSliderConfig
): TimeSelectionInput => {
  const startDate = getMomentDate(timeSliderConfig.startDate);
  const endDate = getMomentDate(timeSliderConfig.endDate);
  const financialYearStartMonth = timeSliderConfig.firstMonthOfYear;
  return match(timeSliderConfig.granularity)
    .with(Granularity.MONTH, () => ({
      calendarYearMonthly: {
        start: timeSliderConfig.startDate,
        end: timeSliderConfig.endDate,
      },
      calendarYearQuarterly: null,
      calendarYearYearly: null,
      financialYearQuarterly: null,
      financialYearYearly: null,
      singleValue: null,
      singleValueByCalendarMonths: null,
      singleValueByCalendarYears: null,
      singleValueByFinancialQuarters: null,
      singleValueByFinancialYears: null,
    }))
    .with(Granularity.QUARTER, () => ({
      calendarYearMonthly: null,
      calendarYearQuarterly: {
        start: {
          quarterOfYear: startDate.quarter(),
          year: startDate.year(),
        },
        end: {
          quarterOfYear: endDate.quarter(),
          year: endDate.year(),
        },
      },
      calendarYearYearly: null,
      financialYearQuarterly: null,
      financialYearYearly: null,
      singleValue: null,
      singleValueByCalendarMonths: null,
      singleValueByCalendarYears: null,
      singleValueByFinancialQuarters: null,
      singleValueByFinancialYears: null,
    }))
    .with(Granularity.YEAR, () => ({
      calendarYearMonthly: null,
      calendarYearQuarterly: null,
      calendarYearYearly: {
        start: startDate.year(),
        end: endDate.year(),
      },
      financialYearQuarterly: null,
      financialYearYearly: null,
      singleValue: null,
      singleValueByCalendarMonths: null,
      singleValueByCalendarYears: null,
      singleValueByFinancialQuarters: null,
      singleValueByFinancialYears: null,
    }))
    .with(Granularity.FINYEAR, () => {
      const startFiscalYear = getStartOfFiscalYear(
        startDate,
        financialYearStartMonth
      ).year();
      const endFiscalYear = getStartOfFiscalYear(
        endDate,
        financialYearStartMonth
      ).year();
      return {
        calendarYearMonthly: null,
        calendarYearQuarterly: null,
        calendarYearYearly: null,
        financialYearQuarterly: null,
        financialYearYearly: {
          start: startFiscalYear,
          end: endFiscalYear,
        },
        singleValue: null,
        singleValueByCalendarMonths: null,
        singleValueByCalendarYears: null,
        singleValueByFinancialQuarters: null,
        singleValueByFinancialYears: null,
      };
    })
    .with(Granularity.FINQUARTER, () => {
      const startFiscalYear = getStartOfFiscalYear(
        startDate,
        financialYearStartMonth
      ).year();
      const startFiscalQuarter = getFiscalQuarter(
        startDate.valueOf(),
        financialYearStartMonth
      );
      const endFiscalYear = getStartOfFiscalYear(
        endDate,
        financialYearStartMonth
      ).year();
      const endFiscalQuarter = getFiscalQuarter(
        endDate.valueOf(),
        financialYearStartMonth
      );
      return {
        calendarYearMonthly: null,
        calendarYearQuarterly: null,
        calendarYearYearly: null,
        financialYearQuarterly: {
          start: {
            quarterOfYear: startFiscalQuarter,
            year: startFiscalYear,
          },
          end: {
            quarterOfYear: endFiscalQuarter,
            year: endFiscalYear,
          },
        },
        financialYearYearly: null,
        singleValue: null,
        singleValueByCalendarMonths: null,
        singleValueByCalendarYears: null,
        singleValueByFinancialQuarters: null,
        singleValueByFinancialYears: null,
      };
    })
    .otherwise(() => {
      throw new Error(
        'Granularity not supported ' + timeSliderConfig.granularity
      );
    });
};

export const singleValueTimeSegment = (
  timeSliderConfig: TimeSliderConfig
): TimeSelectionInput => ({
  calendarYearMonthly: null,
  calendarYearQuarterly: null,
  calendarYearYearly: null,
  financialYearQuarterly: null,
  financialYearYearly: null,
  singleValue: {
    start: timeSliderConfig.startDate,
    end: timeSliderConfig.endDate,
  },
  singleValueByCalendarMonths: null,
  singleValueByCalendarYears: null,
  singleValueByFinancialQuarters: null,
  singleValueByFinancialYears: null,
});
